import {
  ASSIGNMENT_STATE_AUTO,
  ASSIGNMENT_STATE_AUTO_1,
  ASSIGNMENT_STATE_AUTO_3,
  ASSIGNMENT_STATE_BLIND_MINUS,
  ASSIGNMENT_STATE_BLIND_PLUS,
  ASSIGNMENT_STATE_MISSING,
  ASSIGNMENT_STATE_NR,
  ASSIGNMENT_STATE_POST,
  ASSIGNMENT_STATE_TEAM_KO,
  ASSIGNMENT_STATE_TEAM_OK,
  ASSIGNMENT_STATE_TEAM_OK_MISSING,
  ASSIGNMENT_STATE_TEAM_OK_NR,
} from 'lib/assignmentStates'
import { STEP_STATE_COMPLETED_WITH_ERRORS, STEP_STATE_OPEN } from 'lib/stepper'
import { getCarContextKey } from 'lib/carContextKey'

import {
  FILTER_ASSIGNMENT_CAPS_BLIND_OFFERS,
  FILTER_ASSIGNMENT_CAPS_BLIND_NEGATIVE_OFFERS,
  FILTER_ASSIGNMENT_CAPS_BLIND_POSITIVE_OFFERS,
  FILTER_ASSIGNMENT_CAPS_FIRST_CHOICE,
  FILTER_ASSIGNMENT_CAPS_SECOND_CHOICE,
} from 'hooks/useCarsFilters'

export const ASSIGNMENT_ROOT = 'assignment'

export const ASSIGNMENT_STEP_PUBLICATION = 'publication'
export const ASSIGNMENT_STEP_CAR_ASSIGNMENT = 'car-assignment'
export const ASSIGNMENT_STEP_CAP_MANAGEMENT = 'cap-management'
export const ASSIGNMENT_CAR_DETAILS = 'car-details'

export const ASSIGNMENT_STEPS_LIST = [
  ASSIGNMENT_STEP_PUBLICATION,
  ASSIGNMENT_STEP_CAR_ASSIGNMENT,
  ASSIGNMENT_STEP_CAP_MANAGEMENT,
]

export const SESSIONS_ACTIVE_DEPENDENCIES = {
  [ASSIGNMENT_STEP_PUBLICATION]: [],
  [ASSIGNMENT_STEP_CAR_ASSIGNMENT]: [ASSIGNMENT_STEP_PUBLICATION],
  [ASSIGNMENT_STEP_CAP_MANAGEMENT]: [ASSIGNMENT_STEP_PUBLICATION],
}

export const ASSIGNMENT_CAP_MANAGEMENT_DELTA_EQUAL = 'equal'
export const ASSIGNMENT_CAP_MANAGEMENT_DELTA_UNDER = 'under'
export const ASSIGNMENT_CAP_MANAGEMENT_DELTA_OVER = 'over'

export const ASSIGNMENT_CAP_MANAGEMENT_DELTA_LIST = [
  ASSIGNMENT_CAP_MANAGEMENT_DELTA_EQUAL,
  ASSIGNMENT_CAP_MANAGEMENT_DELTA_UNDER,
  ASSIGNMENT_CAP_MANAGEMENT_DELTA_OVER,
]

export const ASSIGNMENT_PHASE_STANDARD = 'standard'
export const ASSIGNMENT_PHASE_REPAIR = 'repair'

export const BLIND_ORIGIN_NO_BLIND = 'no_blind'
export const BLIND_ORIGIN_POSITIVE = 'blind_positive'
export const BLIND_ORIGIN_NEGATIVE = 'blind_negative'

const ASSIGNMENT_PHASE_LABELS = {
  [ASSIGNMENT_PHASE_STANDARD]: 'Standard',
  [ASSIGNMENT_PHASE_REPAIR]: 'Riparazione',
}

const BLIND_ORIGIN_LABELS = {
  [BLIND_ORIGIN_NO_BLIND]: 'No Blind',
  [BLIND_ORIGIN_POSITIVE]: 'Blind +',
  [BLIND_ORIGIN_NEGATIVE]: 'Blind -',
}

export const getAssignmentStepUrl = (sessionId, step, plate) =>
  plate
    ? `/${ASSIGNMENT_ROOT}/${sessionId}/${step}/${plate}`
    : `/${ASSIGNMENT_ROOT}/${sessionId}/${step}`

export const getLastAssignementStep = (sessionId, steps) =>
  `/${ASSIGNMENT_ROOT}/${sessionId}/${getLastAssignmentStep(steps)}`

export const getLastAssignmentStep = (steps) =>
  steps.find(
    (step) =>
      step.state === STEP_STATE_OPEN ||
      step.state === STEP_STATE_COMPLETED_WITH_ERRORS
  )?.step || ASSIGNMENT_STEP_PUBLICATION

export const checkAssignment = (session) => !!session.assignmentStartedAt

export const getAssignmentStatusColor = (status) =>
  [
    ASSIGNMENT_STATE_NR,
    ASSIGNMENT_STATE_MISSING,
    ASSIGNMENT_STATE_TEAM_KO,
  ].includes(status)
    ? 'error'
    : 'primary'

export const getAverage = (list) => {
  const sum = list?.reduce((previous, current) => previous + current, null)

  return sum && Math.round((sum / list.length) * 10) / 10
}

const listIncludesPartner = (list = [], partnerId) =>
  Array.isArray(list)
    ? list.includes(partnerId)
    : typeof list === 'string'
    ? list.includes(partnerId)
    : false

const hasChoiceByType = (
  car,
  partnerId,
  { totalKey, listKey, isBlind = false, isNonBlind = false }
) => {
  const hasTotal = Number(car?.[totalKey] || 0) > 0
  const hasPartner = partnerId
    ? listIncludesPartner(car?.[listKey], partnerId)
    : true
  const blindCheck = isBlind ? car?.assignmentType === 'blind' : true
  const nonBlindCheck = isNonBlind ? car?.assignmentType !== 'blind' : true

  return hasTotal && hasPartner && blindCheck && nonBlindCheck
}

const hasBlindPlusStatus = (car, partnerId) =>
  getAutoAssignmentDisplayState(car) === ASSIGNMENT_STATE_BLIND_PLUS &&
  (partnerId
    ? car?.assignedPartnerId === partnerId ||
      listIncludesPartner(car?.firstChoiceList, partnerId)
    : true)

const hasBlindMinusStatus = (car, partnerId) =>
  getAutoAssignmentDisplayState(car) === ASSIGNMENT_STATE_BLIND_MINUS &&
  (partnerId
    ? car?.assignedPartnerId === partnerId ||
      listIncludesPartner(car?.secondChoiceList, partnerId)
    : true)

export const checkRecapChoice = (car, partnerId, type) => {
  const hasFirstChoice = hasChoiceByType(car, partnerId, {
    totalKey: 'firstChoiceTotal',
    listKey: 'firstChoiceList',
    isNonBlind: true,
  })
  const hasSecondChoice = hasChoiceByType(car, partnerId, {
    totalKey: 'secondChoiceTotal',
    listKey: 'secondChoiceList',
    isNonBlind: true,
  })
  const hasBlindPlus = hasBlindPlusStatus(car, partnerId)
  const hasBlindMinus = hasBlindMinusStatus(car, partnerId)

  if (type === FILTER_ASSIGNMENT_CAPS_FIRST_CHOICE) {
    return hasFirstChoice
  }
  if (type === FILTER_ASSIGNMENT_CAPS_SECOND_CHOICE) {
    return hasSecondChoice
  }
  if (type === FILTER_ASSIGNMENT_CAPS_BLIND_POSITIVE_OFFERS) {
    return hasBlindPlus
  }
  if (type === FILTER_ASSIGNMENT_CAPS_BLIND_NEGATIVE_OFFERS) {
    return hasBlindMinus
  }
  if (type === FILTER_ASSIGNMENT_CAPS_BLIND_OFFERS) {
    return hasBlindPlus
  }

  return !hasFirstChoice && !hasSecondChoice && !hasBlindPlus && !hasBlindMinus
}

export const checkAutomaticAssignment = (car, partnerId) =>
  car.assignmentState === ASSIGNMENT_STATE_AUTO &&
  (partnerId ? car.assignedPartnerId === partnerId : true)

export const checkTeamProposalAssignment = (car, partnerId) =>
  [ASSIGNMENT_STATE_TEAM_OK, ASSIGNMENT_STATE_POST].includes(
    car.assignmentState
  ) && (partnerId ? car.assignedPartnerId === partnerId : true)

export const checkNoProposalAssignment = (car, partnerId) =>
  (partnerId && car.assignedPartnerId !== partnerId) ||
  [
    ASSIGNMENT_STATE_TEAM_KO,
    ASSIGNMENT_STATE_MISSING,
    ASSIGNMENT_STATE_NR,
  ].includes(car.assignmentState)

export const getAssignmentRecapValues = (cars, partnerId) => {
  const result = cars.reduce(
    (previous, car) => ({
      firstChoice:
        (previous.firstChoice || 0) +
        (checkRecapChoice(car, partnerId, FILTER_ASSIGNMENT_CAPS_FIRST_CHOICE)
          ? 1
          : 0),
      secondChoice:
        (previous.secondChoice || 0) +
        (checkRecapChoice(car, partnerId, FILTER_ASSIGNMENT_CAPS_SECOND_CHOICE)
          ? 1
          : 0),
      blindOffers:
        (previous.blindOffers || 0) +
        (checkRecapChoice(car, partnerId, FILTER_ASSIGNMENT_CAPS_BLIND_OFFERS)
          ? 1
          : 0),
      blindPositiveOffers:
        (previous.blindPositiveOffers || 0) +
        (checkRecapChoice(
          car,
          partnerId,
          FILTER_ASSIGNMENT_CAPS_BLIND_POSITIVE_OFFERS
        )
          ? 1
          : 0),
      blindNegativeOffers:
        (previous.blindNegativeOffers || 0) +
        (checkRecapChoice(
          car,
          partnerId,
          FILTER_ASSIGNMENT_CAPS_BLIND_NEGATIVE_OFFERS
        )
          ? 1
          : 0),
      notChosen:
        (previous.notChosen || 0) + (checkRecapChoice(car, partnerId) ? 1 : 0),
      automatic:
        (previous.automatic || 0) +
        (checkAutomaticAssignment(car, partnerId) ? 1 : 0),
      teamProposal:
        (previous.teamProposal || 0) +
        (checkTeamProposalAssignment(car, partnerId) ? 1 : 0),
      noProposal:
        (previous.noProposal || 0) +
        (checkNoProposalAssignment(car, partnerId) ? 1 : 0),
    }),
    {}
  )
  result.total = cars.length

  const appealings = cars
    .map((car) =>
      !partnerId || car?.assignedPartnerId === partnerId ? car.appealing : null
    )
    .filter((appealing) => appealing)

  result.appealing = getAverage(appealings)

  return result
}

export const getDelta = (list, partner, excludeState, session) =>
  list.filter(
    (car) =>
      car.assignedPartnerId === partner.partnerId &&
      (excludeState ? car.assignmentState !== excludeState : true)
  ).length -
  (partner.cap ||
    session.assignmentCaps.find((cap) => cap.partnerId === partner.partnerId)
      ?.cap)

export const handleDeltaErrors = (list, partners, excludeState, session) => {
  partners = Array.isArray(partners) ? partners : [partners]

  return partners.reduce(
    (previous, partner) =>
      previous + (getDelta(list, partner, excludeState, session) > 0 ? 1 : 0),
    0
  )
}

export const getAutoAssignmentDisplayState = (car) =>
  car?.assignmentType === 'blind' &&
  car?.autoAssignmentState === ASSIGNMENT_STATE_AUTO_1
    ? ASSIGNMENT_STATE_BLIND_PLUS
    : car?.assignmentType === 'blind' &&
      car?.autoAssignmentState === ASSIGNMENT_STATE_AUTO_3
    ? ASSIGNMENT_STATE_BLIND_MINUS
    : car?.autoAssignmentState

export const getAssignmentPhase = (car, session) => {
  const explicitPhase = car?.assignmentPhase || car?.assignment_phase
  if (ASSIGNMENT_PHASE_LABELS[explicitPhase]) {
    return explicitPhase
  }

  const sessionKind = session?.sessionKind || session?.session_kind
  if (ASSIGNMENT_PHASE_LABELS[sessionKind]) {
    return sessionKind
  }

  return ASSIGNMENT_PHASE_STANDARD
}

export const getBlindOrigin = (car) => {
  const explicitOrigin = car?.blindOrigin || car?.blind_origin
  if (BLIND_ORIGIN_LABELS[explicitOrigin]) {
    return explicitOrigin
  }

  const autoAssignmentDisplayState = getAutoAssignmentDisplayState(car)
  if (autoAssignmentDisplayState === ASSIGNMENT_STATE_BLIND_MINUS) {
    return BLIND_ORIGIN_NEGATIVE
  }
  if (autoAssignmentDisplayState === ASSIGNMENT_STATE_BLIND_PLUS) {
    return BLIND_ORIGIN_POSITIVE
  }

  return car?.assignmentType === 'blind'
    ? BLIND_ORIGIN_POSITIVE
    : BLIND_ORIGIN_NO_BLIND
}

export const getAssignmentPhaseLabel = (t, car, session) => {
  const phase = getAssignmentPhase(car, session)
  const fallback = ASSIGNMENT_PHASE_LABELS[phase]

  return t
    ? t(`assignment.metadata.phase.${phase}`, { defaultValue: fallback })
    : fallback
}

export const getBlindOriginLabel = (t, car) => {
  const origin = getBlindOrigin(car)
  const fallback = BLIND_ORIGIN_LABELS[origin]

  return t
    ? t(`assignment.metadata.blindOrigin.${origin}`, {
        defaultValue: fallback,
      })
    : fallback
}

const isPresentValue = (value) =>
  value !== null && value !== undefined && value !== ''

const toInteger = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 0
}

export const getAssignmentChoiceTotalsByPlate = (cars = []) =>
  cars.reduce((acc, car) => {
    const contextKey = getCarContextKey(car)
    if (!contextKey) {
      return acc
    }

    const current = acc[contextKey] || { firstChoice: 0, blindOffers: 0 }

    if (
      car?.firstChoicePartnersCount !== null &&
      car?.firstChoicePartnersCount !== undefined
    ) {
      current.firstChoice = Math.max(
        current.firstChoice,
        toInteger(car?.firstChoicePartnersCount)
      )
    }
    if (
      car?.blindOfferPartnersCount !== null &&
      car?.blindOfferPartnersCount !== undefined
    ) {
      current.blindOffers = Math.max(
        current.blindOffers,
        toInteger(car?.blindOfferPartnersCount)
      )
    }

    if (car?.assignmentType === 'blind') {
      const blindOffersCount = Math.max(
        toInteger(car?.firstChoiceTotal),
        isPresentValue(car?.blindFinalPrice) ? 1 : 0
      )
      current.blindOffers = Math.max(current.blindOffers, blindOffersCount)
    } else {
      current.firstChoice = Math.max(
        current.firstChoice,
        toInteger(car?.firstChoiceTotal)
      )
    }

    acc[contextKey] = current
    return acc
  }, {})

export const getAssignmentChoiceTotalsForCar = (
  assignmentChoiceTotalsByPlate,
  car,
  fallbackSessionId = null
) => {
  const directContextKey = getCarContextKey(car)
  if (directContextKey && assignmentChoiceTotalsByPlate?.[directContextKey]) {
    return assignmentChoiceTotalsByPlate[directContextKey]
  }

  const fallbackContextKey = fallbackSessionId
    ? getCarContextKey(car, fallbackSessionId)
    : null
  if (
    fallbackContextKey &&
    assignmentChoiceTotalsByPlate?.[fallbackContextKey]
  ) {
    return assignmentChoiceTotalsByPlate[fallbackContextKey]
  }

  return assignmentChoiceTotalsByPlate?.[car?.plate] || {}
}

export const getCarFinalPrice = (car) => {
  if (car?.assignmentType === 'blind' && isPresentValue(car?.blindFinalPrice)) {
    return car.blindFinalPrice
  }

  return car?.finalPrice
}

export const getAssignmentState = (car) =>
  car?.autoAssignmentPartnerId === car?.assignedPartnerId
    ? getAutoAssignmentDisplayState(car)
    : car?.autoAssignmentState === ASSIGNMENT_STATE_NR
    ? ASSIGNMENT_STATE_TEAM_OK_NR
    : car?.autoAssignmentState === ASSIGNMENT_STATE_MISSING
    ? ASSIGNMENT_STATE_TEAM_OK_MISSING
    : car?.assignedPartnerId === null
    ? ASSIGNMENT_STATE_TEAM_KO
    : ASSIGNMENT_STATE_POST
